import LetsStart from "../form/lets-start";
import LeadingCompany from "../leading-company-cta";
import React from "react";
import Link from "next/link";
import Head from "next/head";
import { useTranslation } from "next-i18next";

type Props = {
    title: React.ReactNode;
    description: string,
    img: string,
    imgAlt: string,
    /** Intrinsic width/height of `img` (used for aspect ratio + CLS) */
    imgWidth?: number,
    imgHeight?: number,
    imgMobile?: string,
    imgSizes?: string,
    hasActions?: boolean,
    className?: string,
    isVideo?: boolean,
    tips?: string,
    hasActionsDemo?: boolean,
    hasActionsDocsLink?: string,
    /** LCP: emit responsive preloads; keep true on product heros (default) */
    preloadImg?: boolean,
};

const ProductHeroSection: React.FC<Props> = ({
    title,
    description,
    img,
    imgAlt,
    imgWidth = 1200,
    imgHeight = 800,
    imgMobile,
    imgSizes,
    hasActions,
    className,
    isVideo,
    tips,
    hasActionsDemo,
    hasActionsDocsLink,
    preloadImg,
}) => {
    const { t } = useTranslation("common");
    const shouldPreloadLcp = preloadImg !== false;
    return (
        <>
            {shouldPreloadLcp && !isVideo && (
                <Head>
                    {imgMobile ? (
                        <>
                            <link
                                rel="preload"
                                as="image"
                                href={imgMobile}
                                media="(max-width: 768px)"
                            />
                            <link
                                rel="preload"
                                as="image"
                                href={img}
                                media="(min-width: 769px)"
                            />
                        </>
                    ) : (
                        <link rel="preload" as="image" href={img} />
                    )}
                </Head>
            )}
            <section className={`hero-section product-page ${className}`}>
                <div className={'container'}>
                    <div className={"d-flex"}>
                        <div className={"left-block"}>
                            <div className={"hero-child"}>
                                {title}
                                <p>{description}</p>
                                {hasActions ? (
                                    <div className="d-flex">
                                        <Link
                                            href="https://app.middleware.io/auth/register/"
                                            target="_blank"
                                            className="sign-up ahref-btn primary"
                                        >
                                            Get Started Free
                                        </Link>

                                        <Link
                                            href="/demo-request/"
                                            className="sign-up ahref-btn secondary"
                                        >
                                            Book a Demo
                                        </Link>
                                    </div>
                                ) : hasActionsDemo ? (
                                    <div className="d-flex">
                                        <Link
                                            href="/demo-request/"
                                            target="_blank"
                                            className="sign-up ahref-btn primary"
                                        >
                                            Get Demo
                                        </Link>

                                        <Link
                                            href={hasActionsDocsLink ? hasActionsDocsLink : "https://docs.middleware.io/"}
                                            className="sign-up ahref-btn secondary"
                                        >
                                            Installation Docs
                                        </Link>
                                    </div>
                                ) : (
                                    <LetsStart />
                                )}
                                {tips && <p className={"tips"}>{tips}</p>}
                            </div>
                            <LeadingCompany />
                        </div>
                        <div className={"right-block"}>
                            {isVideo ? <video
                                playsInline
                                autoPlay
                                muted
                                loop
                                preload="auto"
                                width="100%"
                                height={450}
                                controls={false}
                            >
                                <source src={img} type="video/mp4" />
                            </video>
                                : imgMobile ? (
                                <picture>
                                    <source
                                        media="(max-width: 768px)"
                                        srcSet={imgMobile}
                                    />
                                    <img
                                        className="product-hero-lcp"
                                        src={img}
                                        alt={imgAlt}
                                        width={imgWidth}
                                        height={imgHeight}
                                        fetchPriority="high"
                                        loading="eager"
                                        decoding="sync"
                                    />
                                </picture>
                                ) : (
                                <img
                                    className="product-hero-lcp"
                                    src={img}
                                    alt={imgAlt}
                                    width={imgWidth}
                                    height={imgHeight}
                                    fetchPriority="high"
                                    loading="eager"
                                    decoding="sync"
                                />
                                )}

                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default ProductHeroSection;